import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Trash2, Clock } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/quotationApi";

const ROLES = ["admin", "manager", "staff"];
const ROLE_DESC = {
  admin: "Full control incl. team & billing",
  manager: "Manage pricing, branding & quotes",
  staff: "Create & edit quotations only",
};

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/members");
      setMembers(r.data.members || []);
    } catch (e) {
      console.error("Failed to load members:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setInviting(true);
    try {
      const r = await api.post("/members/invite", { email: email.trim(), role });
      toast.success(
        r.data.status === "pending"
          ? "Invite sent — they'll join when they sign in with Google"
          : "Member added"
      );
      setEmail("");
      setRole("staff");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await api.put(`/members/${m.id}`, { role: newRole });
      toast.success("Role updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/members/${m.id}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove member");
    }
  };

  return (
    <div className="min-h-screen app-bg text-white" data-testid="team-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-20">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-6" data-testid="team-back">
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="mb-8">
          <div className="overline text-[#C5A880] mb-2">Team</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Members & roles</h1>
          <p className="text-white/50 mt-2 text-sm">
            Invite teammates by email. They join automatically when they sign in with Google using the invited address.
          </p>
        </div>

        {/* Invite */}
        <div className="card-luxe mb-8" data-testid="invite-card">
          <div className="overline text-[#C5A880] mb-3">Invite a teammate</div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              className="input-luxe flex-1"
              type="email"
              placeholder="teammate@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && invite()}
              data-testid="invite-email-input"
            />
            <select
              className="input-luxe sm:w-40"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="invite-role-select"
            >
              {ROLES.map((r) => (
                <option key={r} value={r} className="bg-[#0A0C10]">
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </select>
            <button
              onClick={invite}
              disabled={inviting}
              className="btn-primary flex items-center justify-center gap-2 sm:w-auto"
              data-testid="invite-submit-btn"
            >
              <UserPlus size={16} /> {inviting ? "Inviting…" : "Invite"}
            </button>
          </div>
          <p className="text-white/40 text-xs mt-2">{ROLE_DESC[role]}</p>
        </div>

        {/* Members */}
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : (
          <div className="space-y-3" data-testid="members-list">
            {members.map((m) => (
              <div
                key={m.id}
                className="card-luxe flex items-center justify-between gap-3"
                data-testid={`member-row-${m.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full border border-white/15 overflow-hidden flex items-center justify-center bg-white/5 shrink-0">
                    {m.picture ? (
                      <img src={m.picture} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-semibold text-[#C5A880]">
                        {(m.name || m.email || "U").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {m.name || m.email}
                      {m.is_self && <span className="text-white/40 text-xs">(you)</span>}
                      {m.status === "pending" && (
                        <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-wide">
                          <Clock size={10} /> Pending
                        </span>
                      )}
                    </div>
                    <div className="text-white/40 text-xs truncate">{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white"
                    value={m.role}
                    onChange={(e) => changeRole(m, e.target.value)}
                    disabled={m.is_self}
                    data-testid={`member-role-${m.id}`}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r} className="bg-[#0A0C10]">
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </option>
                    ))}
                  </select>
                  {!m.is_self && (
                    <button
                      onClick={() => remove(m)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-white/5 transition"
                      data-testid={`member-remove-${m.id}`}
                      aria-label="Remove member"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
